import { handleActions } from 'redux-actions';
import qs from 'qs';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { getFormValues } from 'redux-form';

import endpoints from '../../../config/endpoints';
import { LOCATION_CHANGE, replace } from 'react-router-redux';

import createDatePicker from '../../shared/date-picker';
import http from '../../shared/http';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import { regions } from '../../../../../../enums/regions';

import createFilters from '../../shared/fixed-filters';
import {default as createCalendar, getDateRangeFromCalendar} from '../../shared/calendar';
import csvExport from '../../shared/csvExport';

const calendar = createCalendar({
	namespace: 'payoutRequestTrafficScaling',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(12, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const datepicker = createDatePicker({
	namespace: 'payoutRequestTrafficScaling',
	stateNodeName: 'datepicker',
	defaultValue: {
		start: moment()
			.format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'payoutRequestTrafficScaling',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'admins',
			title: 'Manager name',
			placeholder: 'Manager name',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS,
				params: 'only_managers=true&limit=100&is_show_name=true'
			}
		},
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_IDS
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'country_ids',
			title: 'Countries',
			placeholder: 'Add country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC,
				params: 'limit=100'
			}
		},
		{
			name: 'niches',
			title: 'Niches',
			placeholder: 'Select niches',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_NICHES
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			searchable: false,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		}
	]
});

// action types
const FETCH_DATA_SUCCESS = 'payoutRequestTrafficScaling/fetch-data-success';
const FETCH_DATA_ERROR = 'payoutRequestTrafficScaling/fetch-data-error';

const TOGGLE_CHANGE_DATE_FORM = 'payoutRequestRegister/toggle-change-date-form';

const APPEND_MAX_LEADS_TO_REQUEST = 'payoutRequestTrafficScaling/APPEND_MAX_LEADS_TO_REQUEST';

const SUBMIT_UPDATE_OK = 'payoutRequestTrafficScaling/SUBMIT_UPDATE_OK';
const SUBMIT_UPDATE_NOT_OK = 'payoutRequestTrafficScaling/SUBMIT_UPDATE_NOT_OK';

const FETCH_PAGINATED_SUCCESS = 'payoutRequestTrafficScaling/FETCH_PAGINATED_SUCCESS';
const FETCH_PAGINATED_ERROR = 'payoutRequestTrafficScaling/FETCH_PAGINATED_ERROR';

const PAGINATE = 'payoutRequestTrafficScaling/PAGINATE';

const TOGGLE_PROGRESS_BAR = 'payoutRequestTrafficScaling/toggle-progress-bar';

const APPLY = 'payoutRequestTrafficScaling/apply';

const TOGGLE_SORT = 'payoutRequests/TOGGLE_SORT';

// permissions
export const allowedPermissions = {
	payout_requests_personal_view: 'payout_requests_personal_view',
	payout_requests_view_all: 'payout_requests_view_all',
	payout_requests_view_matrix_and_history: 'payout_requests_view_matrix_and_history',
	payout_requests_review: 'payout_requests_review',
	payout_requests_manager_role: 'payout_requests_manager_role',
	payout_requests_confirm: 'payout_requests_confirm'
};

const table = {
	columns: {
		id: { index: 'id', className: 'short-id-col', title: 'ID', canBeSorted: true, isHiddenForCopy: true },
		day_after_accepted_at: { index: 'day_after_accepted_at', className: '', title: 'Starts at', canBeSorted: true },
		created_at: { index: 'created_at', className: '', title: 'Created at', canBeSorted: true },
		weekly_valid_conversions_count: {
			index: 'weekly_valid_conversions_count', className: '', title: 'Weekly valid conversions', canBeSorted: true
		},
		weekly_expected_cap: {
			index: 'weekly_expected_cap', className: '', title: 'Weekly expected capa', canBeSorted: true
		},
		maxDailyLeads: { index: 'maxDailyLeads', className: '', title: 'Max daily leads', isHiddenForCopy: true },
		manager: { index: 'manager', className: '', title: 'Manager', isHiddenForCopy: true },
		affiliate: { index: 'affiliate', className: '', title: 'Affiliate' },
		affiliate_id: { index: 'affiliate_id', className: '', title: 'Affiliate ID', isHiddenForCopy: true },
		offer: { index: 'offer', className: '', title: 'Offer' },
		country: { index: 'country', className: '', title: 'Country' },
		sid_campaign: { index: 'sid_campaign', className: '', title: 'Sid & Campaign ID', isHiddenForCopy: true },
		last_payout: { index: 'last_payout', className: '', title: 'Last payout', isHiddenForCopy: true },
		amount: { index: 'amount', className: '', title: 'Confirmed payout' },
		admin: { index: 'admin', className: '', title: 'Admin', isHiddenForCopy: true },
		scaling_stopped_at: { index: 'scaling_stopped_at', className: '', title: 'Stopped at', isHiddenForCopy: true },
		actions: { index: 'actions', className: '', title: 'Actions', isHiddenForCopy: true }
	},
	sortByColumn: {
		index: 'id',
		direction: 'desc'
	}
};
// initial state
const initialState = {
	data: null,
	systemBalances: {},
	calendar: calendar.initialState,
	table,
	showProgressBar: false,
	filters: filters.initialState,
	error: false,
	idOfChangeDateForm: null,
	isRequestFormVisible: false,
	datepicker: datepicker.initialState,
	pagination: {
		limit: 100,
		offset: 0
	},
	sortedPayoutSystemsList: []
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			const query = {
				filters: {}
			};
			const { calendar, filters: { selectedFilters } } = getState().payoutRequestTrafficScaling;

			query.sort = selectors.getSortByColumn(getState());
			query.date_range = getDateRangeFromCalendar(calendar);
			query.limit = 10000;
			query.offset = 0;

			query.isOnlyTrafficScaling = true;

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						query.filters[filter.name] = values;
					}
				});
			}

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.REGISTER}?${qs.stringify(query)}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch)
				.then(async action => {
					for (const item of action.payload.data) {
						await http({
							url: `${endpoints.PAYOUT_REQUESTS.BASE}/max-daily-valid-leads?${qs.stringify(item)}`,
							method: 'get',
							addToResponse: item.id,
							actions: [APPEND_MAX_LEADS_TO_REQUEST, FETCH_DATA_ERROR]
						})(dispatch);
					}
				});
		},
		delete: (id, e) => dispatch => {
			e.preventDefault();

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.BASE}/${id}`,
				method: 'delete',
				actions: [SUBMIT_UPDATE_OK, SUBMIT_UPDATE_NOT_OK]
			})(dispatch);
		},
		stop: (id, e) => dispatch => {
			e.preventDefault();

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.BASE}/${id}/stop`,
				method: 'put',
				actions: [SUBMIT_UPDATE_OK, SUBMIT_UPDATE_NOT_OK]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_UPDATE_OK) {
						dispatch(actions.fetchData());
					}
				});
		},
		start: (id, e) => dispatch => {
			e.preventDefault();

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.BASE}/${id}/start`,
				method: 'put',
				actions: [SUBMIT_UPDATE_OK, SUBMIT_UPDATE_NOT_OK]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_UPDATE_OK) {
						dispatch(actions.fetchData());
					}
				});
		},
		fetchPaginatedData: () => (dispatch, getState) => {
			dispatch({ type: PAGINATE });

			const query = {
				filters: {}
			};
			const { calendar, filters: { selectedFilters } } = getState().payoutRequestTrafficScaling;

			query.sort = selectors.getSortByColumn(getState());
			query.date_range = getDateRangeFromCalendar(calendar);
			const pagination = selectors.getPagination(getState());

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						query.filters[filter.name] = values;
					}
				});
			}

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.REGISTER}?${qs.stringify({...query, ...pagination })}`,
				method: 'get',
				actions: [FETCH_PAGINATED_SUCCESS, FETCH_PAGINATED_ERROR]
			})(dispatch);
		},

		download: () => (dispatch, getState) => {
			const { rows } = selectors.getTable(getState());
			const headers = [
				'manager', 'affiliate', 'affiliate_id', 'offer', 'country', 'sid',
				'campaign_id', 'payout_decision', 'currency', 'status', 'amount', 'current_payout'
			];
			const headersMapping = {
				amount: 'requested_amount'
			};

			const columns = rows.map(entity => headers.map(h => entity[h] || '-'));

			const rowsForExport = [].concat([headers.map(h => headersMapping[h] || h)], columns);

			csvExport('Payout_requests_register', rowsForExport);
		},

		apply: () => dispatch => {
			dispatch(actions.fetchData());
		},

		toggleSort: (col) => dispatch => {
			if (!col) {
				return;
			}
			dispatch({ type: TOGGLE_SORT, payload: col });
			dispatch(actions.fetchData());
		},

		clear: () => (dispatch, getState) => {
			dispatch(replace('/payouts/'));
		},
		toggleChangeDateForm: ({ id, accepted_at }) => (dispatch) => {
			dispatch({ type: TOGGLE_CHANGE_DATE_FORM, payload: id });
			dispatch(datepicker.actions.datepickerChange(accepted_at));
			dispatch(datepicker.actions.datepickerApply());
		},
		submitAcceptedDate: (row, data) => (dispatch, getState) => {
			const date = selectors.getDatepicker(getState());
			const hour = moment(row.accepted_at).get('hour');
			const minute = moment(row.accepted_at).get('minute');
			const second = moment(row.accepted_at).get('second');
			const accepted_at = `${date && date.value}T${hour}:${minute}:${second}Z`;

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.REGISTER}/${row.id}`,
				method: 'put',
				data: { accepted_at },
				actions: [SUBMIT_UPDATE_OK, SUBMIT_UPDATE_NOT_OK]
			})(dispatch)
				.then(action => {
					if (action.type === SUBMIT_UPDATE_OK) {
						dispatch(actions.toggleChangeDateForm(row));
						dispatch(actions.fetchData());
					}
				});
		},
		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	calendar.actions,
	datepicker.actions,
	filters.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: action.payload.data,
				showProgressBar: false,
				error: false
			};
		},
		[APPEND_MAX_LEADS_TO_REQUEST]: (state, action) => ({
			...state,
			data: state.data && state.data.map(item =>
				action.meta === item.id
					? ({ ...item, maxDailyLeads: action.payload.data })
					: ({ ...item })
			)
		}),
		[PAGINATE]: (state, action) => {
			return {
				...state,
				pagination: {
					...state.pagination,
					offset: state.pagination.offset + state.pagination.limit
				}
			};
		},
		[TOGGLE_CHANGE_DATE_FORM]: (state, action) => {
			return {
				...state,
				idOfChangeDateForm: state.idOfChangeDateForm === action.payload ? null : action.payload
			};
		},
		[FETCH_PAGINATED_SUCCESS]: (state, action) => {
			return {
				...state,
				data: [].concat(action.payload.data || []).concat(state.data || []),
				showProgressBar: false,
				error: false
			};
		},
		[APPLY]: (state, action) => {
			return {
				...state,
				selectedFilters: {
					...action.payload
				}
			};
		},
		[TOGGLE_SORT]: (state, action) => ({
			...state,
			table: {
				...state.table,
				sortByColumn: {
					...state.table.sortByColumn,
					index: action.payload,
					direction: action.payload === state.table.sortByColumn.index &&
					state.table.sortByColumn.direction === 'asc' ? 'desc' : 'asc'
				}
			}
		}),
		[FETCH_DATA_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar })
	},
	calendar.reducers,
	datepicker.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.payoutRequestTrafficScaling.data,
	getDatepicker: state => datepicker.selectors.getDatePicker(
		state => state.payoutRequestTrafficScaling.datepicker, state => state
	)(state),
	getIsHiddenCopyCols: state => {
		return getFormValues('createPayoutRequestToggleCopyForm')(state)
			&& getFormValues('createPayoutRequestToggleCopyForm')(state).is_hidden_copy;
	},
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.payoutRequestTrafficScaling.calendar, state => state
	)(state),
	getProgressBar: state => state.payoutRequestTrafficScaling.showProgressBar,
	getIdOfChangeDateForm: state => state.payoutRequestTrafficScaling.idOfChangeDateForm,
	getTable: state => {
		const { table, data } = state.payoutRequestTrafficScaling;
		const rows = (data || []).map(item => ({
			...item, created_at: moment(item.created_at).format('DD-MM-YYYY HH:mm:ss')
		}));
		return {
			headers: Object.values(table.columns),
			rows
		};
	},
	getSortByColumn: state => state.payoutRequestTrafficScaling.table.sortByColumn,
	getPagination: state => state.payoutRequestTrafficScaling.pagination,
	getIsRequestFormVisible: state => state.payoutRequestTrafficScaling.isRequestFormVisible,
	getDeniedToCopyWords: state => {
		const { rows } = selectors.getTable(state);

		return []
			.concat(rows.map(item => item.affiliate))
			.concat(rows.map(item => item.created_at));
	},
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getFilters: state => filters.selectors.getFilters(
		state => state.payoutRequestTrafficScaling.filters,
		state => state
	)(state),
	getRequestFormInitialValues: state => ({
		currency: 'usd'
	}),
	getError: state => state.payoutRequestTrafficScaling.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/payout-requests/list-traffic-scaling/index.js